'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { GOOGLE_ADS_CONVERSIONS } from './GoogleAnalytics';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface SharedParams {
  user_type: string;
  intent_pathway: string;
  cancer_area: string;
}

const GTAG_WAIT_MS = 50;
const GTAG_WAIT_TIMEOUT_MS = 5000;

/**
 * Wait briefly for window.gtag to appear (gtag bootstrap may load after first paint).
 * Resolves true if gtag is available, false if timed out.
 */
function waitForGtag(timeoutMs = GTAG_WAIT_TIMEOUT_MS): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.gtag) return Promise.resolve(true);

  return new Promise(resolve => {
    const start = Date.now();
    const id = window.setInterval(() => {
      if (window.gtag) {
        window.clearInterval(id);
        resolve(true);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        window.clearInterval(id);
        resolve(false);
      }
    }, GTAG_WAIT_MS);
  });
}

/**
 * GA Event Tracking component
 * Tracks user interactions and conversions across the site
 */
export default function GAEventTracking() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    const attachListeners = () => {
      if (cancelled || !window.gtag) return;
      const isDebug = process.env.NODE_ENV === 'development';

      // Helper: read URL params passed from Google Ads
      const getParam = (name: string): string => {
        return searchParams?.get(name) || 'unknown';
      };

      // Shared parameters to attach to every event
      const sharedParams: SharedParams = {
        user_type: pathname?.includes('/patient')
          ? 'patient'
          : pathname?.includes('/provider')
            ? 'provider'
            : pathname?.includes('/sponsor')
              ? 'sponsor'
              : 'visitor',
        intent_pathway: getParam('rt_pathway'),
        cancer_area: getParam('rt_cancer'),
      };

      // --- SIGN-UP COMPLETE (success page detection) ---
      if (searchParams?.get('signup') === 'success' || pathname?.includes('/signup/success')) {
        window.gtag('event', 'sign_up_complete', {
          ...sharedParams,
          lead_stage: 'signup',
        });
      }

      // Ads UI name is "About Us", but the conversion target is /patient/programs page view.
      // Fire once per visit when pathname starts with /patient/programs.
      if (pathname?.startsWith('/patient/programs')) {
        const storageKey = 'ads_conv_patient_programs_page_view';
        const alreadyFired =
          typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === '1';
        if (!alreadyFired) {
          try {
            sessionStorage?.setItem(storageKey, '1');
          } catch {
            // ignore storage failures (private mode, etc.)
          }
          if (isDebug) {
            console.log('🔔 Google Ads Conversion Fired:', {
              send_to: GOOGLE_ADS_CONVERSIONS.PATIENT_PROGRAMS_PAGE_VIEW,
              value: 1.0,
              currency: 'USD',
              timestamp: new Date().toISOString(),
            });
          }
          window.gtag('event', 'conversion', {
            send_to: GOOGLE_ADS_CONVERSIONS.PATIENT_PROGRAMS_PAGE_VIEW,
            value: 1.0,
            currency: 'USD',
          });
        }
      }

      // --- PROGRAM SEARCH FORM ---
      const programSearchForm = document.getElementById('program-search-form');
      const handleProgramSearch = () => {
        const eventData = {
          ...sharedParams,
          lead_stage: 'micro',
        };
        if (isDebug) {
          console.log('GA Event Fired:', {
            event: 'program_search',
            parameters: eventData,
            timestamp: new Date().toISOString(),
          });
        }
        window.gtag?.('event', 'program_search', eventData);

        // Fire Google Ads conversion event with proper conversion label
        if (isDebug) {
          console.log('🔔 Google Ads Conversion Fired:', {
            send_to: GOOGLE_ADS_CONVERSIONS.SUBMIT_LEAD_FORM,
            timestamp: new Date().toISOString(),
          });
        }
        window.gtag?.('event', 'conversion', {
          send_to: GOOGLE_ADS_CONVERSIONS.SUBMIT_LEAD_FORM,
        });
      };
      if (programSearchForm) {
        if (isDebug) console.log('Program search form listener attached');
        programSearchForm.addEventListener('submit', handleProgramSearch);
        cleanups.push(() => programSearchForm.removeEventListener('submit', handleProgramSearch));
      }

      // --- REGISTRATION FORMS (AWS Amplify) ---
      const registerForm = document.querySelector('form[data-amplify-form="register"]');
      const handleRegisterSubmit = () => {
        const eventData = {
          ...sharedParams,
          lead_stage: 'signup',
        };
        if (isDebug) {
          console.log('GA Event Fired:', {
            event: 'registration_start',
            parameters: eventData,
            timestamp: new Date().toISOString(),
          });
        }
        window.gtag?.('event', 'registration_start', eventData);
        // Fire Google Ads conversion event with proper conversion label
        window.gtag?.('event', 'conversion', {
          send_to: GOOGLE_ADS_CONVERSIONS.SUBMIT_LEAD_FORM,
        });
      };
      if (registerForm) {
        if (isDebug) console.log('Registration form listener attached');
        registerForm.addEventListener('submit', handleRegisterSubmit);
        cleanups.push(() => registerForm.removeEventListener('submit', handleRegisterSubmit));
      }

      // --- FORM START TRACKING (fires once per form when user starts interacting) ---
      const trackedForms = new Set<string>();
      const handleFormStart = (formId: string, formType: string) => {
        if (trackedForms.has(formId)) return;
        trackedForms.add(formId);

        const eventData = {
          ...sharedParams,
          form_type: formType,
        };
        if (isDebug) {
          console.log('GA Event Fired:', {
            event: 'form_start',
            parameters: eventData,
            timestamp: new Date().toISOString(),
          });
        }
        window.gtag?.('event', 'form_start', eventData);
      };

      // Track form_start for search input focus
      const searchInput = document.querySelector(
        '.rh-searchbar-input input, [data-testid="programs-search-input"] input',
      );
      const handleSearchFocus = () => handleFormStart('search', 'search');
      if (searchInput) {
        searchInput.addEventListener('focus', handleSearchFocus, { once: true });
        cleanups.push(() => searchInput.removeEventListener('focus', handleSearchFocus));
      }

      // Track form_start for contact form (JotForm iframe interaction)
      const contactForm = document.getElementById('contact-form');
      const handleContactFocus = () => handleFormStart('contact', 'contact');
      if (contactForm) {
        contactForm.addEventListener('click', handleContactFocus, { once: true });
        cleanups.push(() => contactForm.removeEventListener('click', handleContactFocus));
      }

      // Track form_start for registration form
      const handleRegisterFocus = () => handleFormStart('register', 'registration');
      if (registerForm) {
        registerForm.addEventListener('focusin', handleRegisterFocus, { once: true });
        cleanups.push(() => registerForm.removeEventListener('focusin', handleRegisterFocus));
      }
    };

    waitForGtag().then(ready => {
      if (cancelled) return;
      if (!ready) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'GAEventTracking: window.gtag not available after wait; listeners not attached',
          );
        }
        return;
      }
      attachListeners();
    });

    return () => {
      cancelled = true;
      cleanups.forEach(fn => fn());
    };
  }, [pathname, searchParams]);

  return null;
}

/**
 * Helper function to track custom events from anywhere in the app
 * Usage: trackGAEvent('button_click', { button_name: 'hero_cta' })
 */
export function trackGAEvent(eventName: string, params?: Record<string, any>) {
  const isDebug = process.env.NODE_ENV === 'development';
  if (typeof window !== 'undefined' && window.gtag) {
    const searchParams = new URLSearchParams(window.location.search);

    const sharedParams: SharedParams = {
      user_type: window.location.pathname.includes('/patient')
        ? 'patient'
        : window.location.pathname.includes('/provider')
          ? 'provider'
          : window.location.pathname.includes('/sponsor')
            ? 'sponsor'
            : 'visitor',
      intent_pathway: searchParams.get('rt_pathway') || 'unknown',
      cancer_area: searchParams.get('rt_cancer') || 'unknown',
    };

    const eventData = {
      ...sharedParams,
      ...params,
    };

    if (isDebug) {
      console.log('GA Event Fired:', {
        event: eventName,
        parameters: eventData,
        timestamp: new Date().toISOString(),
      });
    }

    window.gtag('event', eventName, eventData);
  } else {
    if (isDebug) {
      console.warn('GA Event NOT sent (gtag not available):', eventName, params);
    }
  }
}

/**
 * Fire a Google Ads conversion event
 * Uses the conversion ID/label format required by Google Ads
 * Usage: trackGoogleAdsConversion('AW-17570117616/qPo9CJ-Uu94bEPDvirpB')
 */
export function trackGoogleAdsConversion(
  conversionLabel: string,
  options?: { value?: number; currency?: string },
) {
  const isDebug = process.env.NODE_ENV === 'development';
  if (typeof window !== 'undefined' && window.gtag) {
    const payload: Record<string, string | number> = {
      send_to: conversionLabel,
    };
    if (options?.value !== undefined) payload.value = options.value;
    if (options?.currency) payload.currency = options.currency;
    if (isDebug) {
      console.log('🔔 Google Ads Conversion Fired:', {
        ...payload,
        timestamp: new Date().toISOString(),
      });
    }
    window.gtag('event', 'conversion', payload);
  } else {
    if (isDebug) {
      console.warn('Google Ads Conversion NOT sent (gtag not available):', conversionLabel);
    }
  }
}
